<template>
    <div class="tab-detail">
        <div class="header">
            <div class="title">{{ $t("appStore.apps") }}</div>
            <div class="header-actions">
                <a-button type="primary" @click="syncApps" :loading="syncing">
                    <a-icon type="sync" /> {{ $t("appStore.syncApps") }}
                </a-button>
                <a-button @click="fetchApps" :loading="loading">
                    <a-icon type="reload" /> {{ $t("appStore.refresh") }}
                </a-button>
            </div>
        </div>
        <div class="help-text">
            <a-icon type="info-circle" /> {{ $t("appStore.appsAutoFetch") }}
        </div>
        <div class="apps-container asr-app-list" v-if="!loading">
            <article class="asr-app-list__card" v-for="app in apps" :key="app._id">
                <div class="asr-app-list__main">
                    <div class="asr-app-list__icon">
                        <img v-if="app.iconUrl" :src="app.iconUrl" alt="" />
                        <span class="material-icons" v-else>phone_iphone</span>
                    </div>
                    <div class="asr-app-list__info">
                        <h3 class="asr-app-list__name">{{ app.name }}</h3>
                        <div class="asr-app-list__bundle">{{ app.bundleId }}</div>
                        <div class="asr-app-list__meta">
                            <span v-if="app.totalReviewCount" class="asr-app-list__badge">
                                <span class="material-icons">reviews</span>
                                <span>{{ app.totalReviewCount }} reviews</span>
                            </span>
                            <span v-if="app.primaryLocale" class="asr-app-list__badge">
                                <span class="material-icons">language</span>
                                <span>{{ app.primaryLocale }}</span>
                            </span>
                            <span v-if="app.lastSyncedAt" class="asr-app-list__badge">
                                <span class="material-icons">sync</span>
                                <span>{{ formatDate(app.lastSyncedAt) }}</span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="asr-app-list__actions">
                    <a-button size="small" type="primary" @click="syncReviews(app)" :loading="syncingApp === app._id">
                        <a-icon type="sync" /> {{ $t("appStore.syncReviews") }}
                    </a-button>
                </div>
            </article>
        </div>
        <div class="loading" v-else>
            <a-spin />
        </div>
        <div class="empty" v-if="!loading && apps.length === 0">
            <a-empty :description="$t('appStore.noAppsFound')">
                <a-button type="primary" @click="syncApps" :loading="syncing">
                    {{ $t("appStore.syncApps") }}
                </a-button>
            </a-empty>
        </div>
    </div>
</template>

<script>
import { APP_STORE_API } from "../../../../../api/appstore";
import moment from "moment";

export default {
    name: "AppList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            apps: [],
            loading: false,
            syncing: false,
            syncingApp: null,
        };
    },
    methods: {
        async fetchApps() {
            this.loading = true;
            try {
                const response = await APP_STORE_API.listApps(this.channel._id);
                this.apps = response.data;
            } catch (err) {
                this.$message.error(this.$t("appStore.errorFetchingApps"));
            } finally {
                this.loading = false;
            }
        },
        async syncApps() {
            this.syncing = true;
            try {
                await APP_STORE_API.syncApps(this.channel._id);
                this.$message.success(this.$t("appStore.appsSynced"));
                await this.fetchApps();
            } catch (err) {
                var errorMessage = err.response && err.response.data && err.response.data.message;
                this.$message.error(errorMessage || this.$t("appStore.errorSyncingApps"));
            } finally {
                this.syncing = false;
            }
        },
        async syncReviews(app) {
            this.syncingApp = app._id;
            try {
                await APP_STORE_API.fetchReviews(this.channel._id, app.appId);
                this.$message.success(this.$t("appStore.reviewsSynced"));
                await this.fetchApps();
            } catch (err) {
                this.$message.error(this.$t("appStore.errorSyncingReviews"));
            } finally {
                this.syncingApp = null;
            }
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
    },
    mounted() {
        this.fetchApps();
    },
};
</script>

<style lang="scss" scoped>
.tab-detail {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .header-actions {
            display: flex;
            gap: 10px;
        }
    }

    .help-text {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px;
        background: var(--color-bg-subtle);
        border-radius: 6px;
        font-size: 13px;
        color: var(--color-text-default);
    }

    .apps-container {
        margin-top: 15px;
    }

    /* İzole uygulama listesi: dışarıdan etkilenmez, dışarıyı etkilemez */
    .asr-app-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 380px), 1fr));
        gap: 24px;

        &__card {
            display: flex;
            flex-direction: column;
            gap: 20px;
            padding: 24px;
            margin: 0;
            background: var(--color-bg-subtle, #fafafa);
            border: 1px solid var(--color-border-default, #e5e7eb);
            border-radius: 16px;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
            transition: box-shadow 0.25s ease, border-color 0.25s ease;

            &:hover {
                box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.08);
                border-color: rgba(0, 122, 255, 0.4);
            }
        }

        &__main {
            display: flex;
            align-items: flex-start;
            gap: 18px;
            min-width: 0;
        }

        &__icon {
            width: 56px;
            height: 56px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(145deg, #007aff 0%, #0051d5 50%, #0047b3 100%);
            border-radius: 14px;
            overflow: hidden;
            box-shadow: 0 2px 12px rgba(0, 122, 255, 0.3);

            img {
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            .material-icons {
                font-size: 28px;
                color: #fff;
            }
        }

        &__info {
            flex: 1;
            min-width: 0;
            overflow: hidden;
        }

        &__name {
            margin: 0 0 4px;
            padding: 0;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.3;
            letter-spacing: -0.01em;
            color: var(--color-text-strong-default, #111827);
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
        }

        &__bundle {
            margin: 0 0 12px;
            padding: 0;
            font-size: 11px;
            line-height: 1.4;
            font-family: ui-monospace, "SF Mono", monospace;
            color: var(--color-text-default, #6b7280);
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
        }

        &__meta {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin: 0;
            padding: 0;
            min-width: 0;
        }

        &__badge {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 6px 10px;
            background: rgba(0, 122, 255, 0.08);
            border-radius: 8px;
            font-size: 12px;
            font-weight: 500;
            line-height: 1.2;
            color: var(--color-text-default, #374151);
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;

            span:last-child {
                word-wrap: break-word;
                overflow-wrap: break-word;
                word-break: break-word;
            }

            .material-icons {
                font-size: 14px;
                flex-shrink: 0;
                color: #007aff;
            }
        }

        &__actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 10px;
            padding-top: 4px;
            border-top: 1px solid var(--color-border-default, #e5e7eb);
        }
    }

    .loading,
    .empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }
}
</style>
